
import { useState } from 'react';
import { Task, TaskStatus } from './types';
import { calculateNewPosition } from './taskUtils';
import { 
  insertTaskToSupabase, 
  updateTaskInSupabase, 
  deleteTaskFromSupabase,
  updateTaskPositionsInSupabase 
} from './taskApi';

export const useTaskOperations = (
  tasks: Task[],
  setTasks: React.Dispatch<React.SetStateAction<Task[]>>,
  allTasks: Task[],
  setAllTasks: React.Dispatch<React.SetStateAction<Task[]>>,
  activeTask: Task | null,
  setActiveTask: React.Dispatch<React.SetStateAction<Task | null>>,
  user: any,
  activeListId: string | null,
  isAuthenticated: boolean
) => {
  const [taskToEdit, setTaskToEdit] = useState<Task | null>(null);

  const addTask = async (title: string, detail: string = '') => {
    if (!activeListId || !isAuthenticated) return;
    
    const highestPosition = calculateNewPosition(tasks, 'to-do', activeListId);
    
    const newTask: Task = {
      id: crypto.randomUUID(),
      title: title || 'Untitled Task',
      detail: detail || '',
      status: 'to-do',
      created_at: new Date().toISOString(),
      dateCreated: new Date(),
      list_id: activeListId,
      totalTimeSpent: 0,
      pomodorosCompleted: 0,
      position: highestPosition,
      priority_quadrant: undefined,
      priority_x: undefined,
      priority_y: undefined,
      ...(user && { user_id: user.id }),
    };

    if (user) {
      try {
        await insertTaskToSupabase(newTask, user.id);
      } catch (error) {
        console.error('Error adding task:', error);
      }
    }
    
    setTasks(prev => [newTask, ...prev]);
    setAllTasks(prev => [newTask, ...prev]);
  };

  const updateTask = async (updatedTask: Task) => {
    if (!isAuthenticated) return;
    
    const updatedTasks = tasks.map(task => 
      task.id === updatedTask.id ? { ...task, ...updatedTask } : task
    );
    
    setTasks(updatedTasks);
    
    const updatedAllTasks = allTasks.map(task => 
      task.id === updatedTask.id ? { ...task, ...updatedTask } : task
    );
    setAllTasks(updatedAllTasks);
    
    if (activeTask && activeTask.id === updatedTask.id) {
      setActiveTask({ ...activeTask, ...updatedTask });
    }
    
    if (user) {
      try {
        await updateTaskInSupabase(updatedTask, user.id);
      } catch (error) {
        console.error('Error updating task:', error);
      }
    }
  };

  const deleteTask = async (id: string) => {
    if (!isAuthenticated) return;
    
    const updatedTasks = tasks.filter(task => task.id !== id);
    setTasks(updatedTasks);
    
    const updatedAllTasks = allTasks.filter(task => task.id !== id);
    setAllTasks(updatedAllTasks);
    
    if (activeTask && activeTask.id === id) {
      setActiveTask(null);
    }
    
    if (user) {
      try {
        await deleteTaskFromSupabase(id, user.id);
      } catch (error) {
        console.error('Error deleting task:', error);
      }
    }
  };

  const updateTaskTime = (seconds: number, incrementPomodoro = false) => {
    // For anonymous users, skip task time updates since there's no active task or user
    if (!activeTask || !isAuthenticated || !user) {
      console.log('ℹ️ Skipping task time update for anonymous user or no active task');
      return;
    }
    
    const updatedTask = {
      ...activeTask,
      totalTimeSpent: activeTask.totalTimeSpent + seconds,
      pomodorosCompleted: incrementPomodoro 
        ? activeTask.pomodorosCompleted + 1 
        : activeTask.pomodorosCompleted
    };
    
    updateTask(updatedTask);
  };

  const reorderTasks = async (taskId: string, sourceIndex: number, destinationIndex: number, status: TaskStatus) => {
    if (!isAuthenticated || !user) return;
    
    // Get all tasks for the specific status, sorted by position
    const statusTasks = tasks
      .filter(task => task.status === status)
      .sort((a, b) => (a.position || 0) - (b.position || 0));
    
    const taskToMove = statusTasks.find(task => task.id === taskId);
    if (!taskToMove) {
      console.error('Task to move not found:', taskId);
      return;
    }

    // Store original state for rollback
    const originalTasks = [...tasks];
    const originalAllTasks = [...allTasks];

    try {
      // Create a new array with the task moved to the new position
      const reorderedTasks = [...statusTasks];
      reorderedTasks.splice(sourceIndex, 1);
      reorderedTasks.splice(destinationIndex, 0, taskToMove);
      
      // Update positions based on array order
      const updatedStatusTasks = reorderedTasks.map((task, index) => ({
        ...task,
        position: index
      }));
      
      // Update the main tasks array optimistically
      const updatedTasks = tasks.map(task => {
        const updatedTask = updatedStatusTasks.find(ut => ut.id === task.id);
        return updatedTask || task;
      });
      
      setTasks(updatedTasks);
      
      // Update allTasks as well
      const updatedAllTasks = allTasks.map(task => {
        const updatedTask = updatedStatusTasks.find(ut => ut.id === task.id);
        return updatedTask || task;
      });
      setAllTasks(updatedAllTasks);
      
      // Persist to database
      await updateTaskPositionsInSupabase(updatedStatusTasks, user.id);
      console.log('Successfully reordered tasks in database');
      
    } catch (error) {
      console.error('Error updating task positions:', error);
      // Rollback on error
      setTasks(originalTasks);
      setAllTasks(originalAllTasks);
      throw error;
    }
  };

  const updateTaskStatus = async (id: string, newStatus: TaskStatus, newPosition?: number) => {
    if (!isAuthenticated || !user) return;
    
    const taskToUpdate = tasks.find(task => task.id === id);
    if (!taskToUpdate) {
      console.error('Task to update not found:', id);
      return;
    }

    // Store original state for rollback
    const originalTasks = [...tasks];
    const originalAllTasks = [...allTasks];
    
    try {
      // Get tasks in the destination status
      const tasksWithNewStatus = tasks
        .filter(task => task.status === newStatus && task.id !== id)
        .sort((a, b) => (a.position || 0) - (b.position || 0));
      
      let position = newPosition !== undefined ? newPosition : tasksWithNewStatus.length;
      
      // Optimistically update the task status and position
      const updatedTask = { 
        ...taskToUpdate, 
        status: newStatus, 
        position 
      };

      // Update UI immediately
      const updatedTasks = tasks.map(task => 
        task.id === id ? updatedTask : task
      );
      setTasks(updatedTasks);

      const updatedAllTasks = allTasks.map(task => 
        task.id === id ? updatedTask : task
      );
      setAllTasks(updatedAllTasks);

      // Update active task if it's the one being moved
      if (activeTask && activeTask.id === id) {
        setActiveTask(updatedTask);
      }

      // Handle position shifts if inserting at a specific position
      if (newPosition !== undefined) {
        const tasksToShift = tasksWithNewStatus.filter(task => (task.position || 0) >= position);
        
        if (tasksToShift.length > 0) {
          const shiftUpdates = tasksToShift.map(task => ({
            ...task,
            position: (task.position || 0) + 1
          }));
          
          // Update shifted tasks in UI
          const updatedTasksWithShifts = updatedTasks.map(task => {
            const shiftedTask = shiftUpdates.find(st => st.id === task.id);
            return shiftedTask || task;
          });
          setTasks(updatedTasksWithShifts);

          // Persist shift updates to database
          await updateTaskPositionsInSupabase(shiftUpdates, user.id);
        }
      }
      
      // Persist main task update to database
      await updateTaskInSupabase(updatedTask, user.id);
      console.log('Successfully updated task status in database');
      
    } catch (error) {
      console.error('Error updating task status:', error);
      // Rollback on error
      setTasks(originalTasks);
      setAllTasks(originalAllTasks);
      if (activeTask && activeTask.id === id) {
        setActiveTask(taskToUpdate);
      }
      throw error;
    }
  };

  return {
    addTask,
    updateTask,
    deleteTask,
    updateTaskTime,
    reorderTasks,
    updateTaskStatus,
    taskToEdit,
    setTaskToEdit
  };
};
